<template>
  <span>{{ formatted }}</span>
</template>

<script setup>
  import { gsap } from 'gsap'
  import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'

  const props = defineProps({
    duration: { default: 1, type: Number },
    formatter: { default: (n) => n, type: Function },
    value: { default: 0, type: [Number, String, null] },
  })

  const displayValue = ref(0) // what we render (number or string)
  const tweenTarget = { n: 0 } // plain object GSAP can animate
  let tween = null // current gsap tween handle

  function isFiniteNumber(v) {
    return Number.isFinite(typeof v === 'number' ? v : Number(v))
  }
  const toNumber = (v) => (typeof v === 'number' ? v : Number(v))

  // initialize
  if (isFiniteNumber(props.value)) {
    const n = Math.ceil(toNumber(props.value))
    tweenTarget.n = n
    displayValue.value = n
  } else {
    displayValue.value = props.value ?? ''
  }

  const formatted = computed(() => {
    // For numbers, run through formatter; for strings, show as-is
    if (typeof displayValue.value === 'number') {
      return props.formatter(Math.round(displayValue.value))
    }
    const s = displayValue.value
    return s === null || s === undefined || String(s) === '' ? '--' : String(s)
  })

  function animateTo(next) {
    stopTween()

    // No duration or no change: set immediately
    if (props.duration <= 0 || Math.round(tweenTarget.n) === Math.round(next)) {
      tweenTarget.n = next
      displayValue.value = next
      return
    }

    tween = gsap.to(tweenTarget, {
      duration: props.duration,
      ease: 'power2.inOut',
      n: Math.ceil(next),
      onComplete: () => {
        displayValue.value = Math.round(next)
      },
      onUpdate: () => {
        displayValue.value = Math.round(tweenTarget.n)
      },
    })
  }

  function stopTween() {
    if (tween) {
      tween.kill()
      tween = null
    }
  }

  watch(toRef(props, 'value'), (v) => {
    if (isFiniteNumber(v)) {
      animateTo(toNumber(v))
    } else {
      // Non-numeric: show it as a string and stop any numeric tween
      stopTween()
      displayValue.value = v ?? ''
    }
  })

  onBeforeUnmount(stopTween)
</script>
